import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { api, useAuth } from '../../context/AuthContext';
import { usePortalDockSichtbar, portalAktualisieren } from './PortalDock';
import { dateiFeld } from '../upload/DateiFelderDrop';

const kontext = async (pathname, mnr) => {
  const m = pathname.match(/^\/portal\/akten\/([^/?#]+)/);
  if (m) { try { const a = (await api.get(`/portal/akten/${m[1]}`)).data.akte || {}; return { case_id: m[1], ref: a.aktenzeichen || `M-${mnr}`, label: a.aktenzeichen ? `Akte ${a.aktenzeichen}` : 'Ihre Akte' }; } catch { /* allgemeine Ablage */ } }
  return { case_id: '', ref: mnr ? `M-${mnr}` : 'Portal-Upload', label: 'Ihre Unterlagen' };
};

// Mandantenportal: Dateien auf jede Portalseite ziehen – landen sicher bei der Kanzlei (in der geöffneten Akte)
export const PortalAblage = () => {
  const sichtbar = usePortalDockSichtbar();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [ueber, setUeber] = useState(false);
  const [liste, setListe] = useState([]);
  const pfad = useRef(pathname);
  pfad.current = pathname;
  const hoch = useRef(null);
  hoch.current = async (dateien) => {
    const start = liste.length;
    setListe((l) => [...l, ...dateien.map((f) => ({ name: f.name, busy: true }))]);
    const ctx = await kontext(pfad.current, user?.mandanten_nr);
    for (let i = 0; i < dateien.length; i += 1) {
      const fd = new FormData();
      fd.append('files', dateien[i]); fd.append('mandate_ref', ctx.ref); fd.append('register_case_id', ctx.case_id);
      let erg;
      try { await api.post('/documents/upload-multi', fd, { timeout: 180000 }); erg = { text: `übermittelt · ${ctx.label}` }; } catch (x) { erg = { fehler: x?.response?.data?.detail || 'fehlgeschlagen' }; }
      setListe((l) => l.map((y, j) => (j === start + i ? { name: y.name, ...erg } : y)));
    }
    portalAktualisieren();
    window.dispatchEvent(new Event('ablage-fertig'));
  };

  useEffect(() => {
    if (!sichtbar) return undefined;
    let tiefe = 0;
    const istDatei = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
    const enter = (e) => { if (istDatei(e)) tiefe += 1; };
    const leave = (e) => { if (!istDatei(e)) return; tiefe = Math.max(0, tiefe - 1); if (!tiefe) setUeber(false); };
    const over = (e) => { if (!istDatei(e)) return; e.preventDefault(); setUeber(!dateiFeld(e.target) && !e.target?.closest?.('[data-eigene-ablage]')); };
    const drop = (e) => {
      tiefe = 0; setUeber(false);
      if (e.defaultPrevented || !istDatei(e)) return;
      e.preventDefault();
      if (e.dataTransfer.files.length) hoch.current([...e.dataTransfer.files]);
    };
    window.addEventListener('dragenter', enter); window.addEventListener('dragleave', leave); window.addEventListener('dragover', over); window.addEventListener('drop', drop);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragleave', leave); window.removeEventListener('dragover', over); window.removeEventListener('drop', drop); };
  }, [sichtbar]);

  if (!sichtbar) return null;
  return (<>
    <input type="file" multiple className="hidden" data-testid="portal-ablage-datei" onChange={(e) => { const f = [...e.target.files]; e.target.value = ''; if (f.length) hoch.current(f); }} />
    {ueber && (
      <div className="fixed inset-0 z-[65] pointer-events-none flex items-center justify-center p-6 bg-[#0B1724]/30 backdrop-blur-[2px]" data-testid="portal-ablage-overlay">
        <div className="rounded-2xl border-2 border-dashed border-[#e0b56a] bg-[#0B1724]/95 text-white px-8 py-6 flex items-center gap-4 shadow-2xl">
          <UploadCloud className="w-8 h-8 text-[#e0b56a]" />
          <div><p className="font-serif text-2xl">Datei loslassen</p><p className="text-xs text-white/70 mt-0.5">Wird sicher und verschlüsselt an die Kanzlei übermittelt{/^\/portal\/akten\/./.test(pathname) ? ' – direkt in diese Akte' : ''}.</p></div>
        </div>
      </div>)}
    {liste.length > 0 && (
      <div className="fixed bottom-24 right-4 z-[70] w-[min(380px,calc(100vw-2rem))] rounded-2xl bg-[#0B1724] text-white shadow-2xl border border-[#b8893d]/40 p-4" data-testid="portal-ablage-ergebnisse">
        <div className="flex items-center gap-2 mb-2"><UploadCloud className="w-4 h-4 text-[#e0b56a]" /><p className="text-sm font-semibold flex-1">Hochgeladen</p>
          <button type="button" onClick={() => setListe([])} className="p-1 text-white/60 hover:text-white" aria-label="Schließen" data-testid="portal-ablage-schliessen"><X className="w-4 h-4" /></button></div>
        <ul className="space-y-1.5 max-h-60 overflow-y-auto">{liste.map((x, i) => (
          <li key={`${x.name}-${i}`} className="flex items-start gap-2 text-xs" data-testid={`portal-ablage-eintrag-${i}`}>
            {x.busy ? <Loader2 className="w-3.5 h-3.5 mt-0.5 animate-spin text-[#e0b56a]" /> : x.fehler ? <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-400" /> : <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400" />}
            <span className="min-w-0 flex-1"><span className="block truncate text-white/60">{x.name}</span>{x.busy ? 'wird hochgeladen …' : x.fehler ? <span className="text-amber-300">Fehler: {x.fehler}</span> : <Link to="/dokumente" className="text-[#e0b56a] hover:underline">{x.text}</Link>}</span>
          </li>))}</ul>
      </div>)}
  </>);
};

export const PortalAblageStreifen = ({ text }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dashed border-[#b8893d]/50 bg-[#fbf7f0] px-4 py-3 text-sm text-[#34536b]" data-testid="portal-ablage-streifen">
    <UploadCloud className="w-5 h-5 text-[#b8893d] shrink-0" />
    <span className="flex-1 min-w-[12rem]">{text}</span>
    <button type="button" onClick={() => document.querySelector('[data-testid="portal-ablage-datei"]')?.click()} className="h-9 px-4 rounded-[10px] bg-[#1B3A5C] text-white text-xs hover:bg-[#2A5280] transition-colors" data-testid="portal-ablage-streifen-waehlen">Dateien wählen</button>
  </div>
);

export default PortalAblage;
